import Typography from "#/components/shared/typography";
import { MedicalRecordRequest } from "#/api/medical-record-requests.type";
import Card from "#/components/shared/card";
import { CheckIcon } from "@heroicons/react/24/solid";

type StatusStepKey =
  | "pending"
  | "ready_to_review"
  | "lookup"
  | "ready_to_send"
  | "fax"
  | "department"
  | "completed";

const statusToStepMap: Record<string, StatusStepKey> = {
  pending: "pending",
  ready_to_review: "ready_to_review",
  ready_to_send: "ready_to_send",
  lookup_in_progress: "lookup",
  lookup_failed: "lookup",
  no_fax_found: "lookup",
  fax_sending: "fax",
  fax_sent: "fax",
  fax_failed: "fax",
  completed: "completed",
  canceled: "completed",
  department_not_attempted: "department",
  department_contacted: "department",
  department_contact_attempted: "department",
  department_confirmed_receipt: "department",
  department_confirmed_processed: "department",
  department_rejected: "department",
};

type StatusStep = {
  key: StatusStepKey;
  label: string;
  description: string;
  optional?: boolean;
};

const steps: StatusStep[] = [
  {
    key: "pending",
    label: "Draft / Pending",
    description:
      "Request created. Waiting for details (patient info, recipient, fax/email).",
  },
  {
    key: "ready_to_review",
    label: "Ready to review",
    description:
      "Admin needs to confirm contact details (fax/email) and validate the request before sending.",
  },
  {
    key: "lookup",
    label: "Fax lookup (optional)",
    description:
      "Admin is searching for a fax number. May not occur for all requests. If lookup fails, add a fax manually or choose email.",
    optional: true,
  },
  {
    key: "ready_to_send",
    label: "Ready to send",
    description:
      "All details present. Ready for delivery via fax or email. Mark as ready or send now.",
  },
  {
    key: "fax",
    label: "Fax delivery",
    description:
      "Sending request via fax. Includes sending, sent, or failed states. If fax fails, retry or switch to email.",
  },
  {
    key: "department",
    label: "Department outreach (optional)",
    description:
      "Admin reaches out to the medical records department. Covers contacting, attempts, confirmation, or rejection.",
    optional: true,
  },
  {
    key: "completed",
    label: "Completed",
    description: "Request completed, closed, or canceled.",
  },
];

function toTitleCase(value: string) {
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function getStepForStatus(status: string | null | undefined): StatusStepKey {
  if (!status) return "pending";
  return statusToStepMap[status] ?? "pending";
}

function getStepState(
  step: StatusStep,
  currentStep: StatusStepKey
): "complete" | "current" | "upcoming" {
  const order = steps.map((s) => s.key);
  const currentIdx = order.indexOf(currentStep);
  const stepIdx = order.indexOf(step.key);
  if (stepIdx < currentIdx) return "complete";
  if (stepIdx === currentIdx) return "current";
  return "upcoming";
}

function renderStatusPill(state: "complete" | "current" | "upcoming") {
  if (state === "complete") return "bg-emerald-600";
  if (state === "current") return "bg-terracotta-500";
  return "bg-cream-300";
}

export function MedicalRecordRequestStatusTracker({
  request,
}: {
  request: MedicalRecordRequest;
}) {
  const currentStep = getStepForStatus(request.status);
  const rawStatus = request.status;

  return (
    <Card variant="default" elevation={1}>
      <Card.Header variant="default">
        <Typography variant="h4" className="text-terracotta">
          Request status
        </Typography>
        <Typography variant="body2" className="text-brown-600">
          Current state: {toTitleCase(rawStatus ?? "unknown")}
        </Typography>
      </Card.Header>
      <Card.Body className="space-y-4">
        <div className="relative">
          <div className="absolute left-0 right-0 top-4 hidden h-1 rounded bg-cream-200 md:block" />
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            {steps.map((step, index) => {
              const state = getStepState(step, currentStep);
              // Show optional steps inline but only as current/completed if relevant
              if (step.optional && state === "upcoming") {
                const currentStatusStep = getStepForStatus(rawStatus);
                if (currentStatusStep !== step.key) {
                  return null;
                }
              }
              const markerClass = renderStatusPill(state);
              return (
                <div
                  key={step.key}
                  className="relative flex flex-1 flex-col items-start gap-2 md:items-center"
                >
                  <div className="flex items-center gap-2">
                    <div
                      className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold text-white ${markerClass}`}
                    >
                      {state === "complete" ||
                      (state === "current" && step.key === "completed") ? (
                        <CheckIcon className="h-4 w-4" aria-hidden />
                      ) : (
                        index + 1
                      )}
                    </div>
                    <div className="md:hidden">
                      <Typography variant="subtitle1" className="text-terracotta-800">
                        {step.label}
                      </Typography>
                      <Typography variant="caption" className="text-brown-600">
                        {step.optional ? "Optional" : ""}
                      </Typography>
                    </div>
                  </div>
                  <div className="hidden md:flex md:flex-col md:items-center md:text-center">
                    <Typography variant="subtitle2" className="text-terracotta-800">
                      {step.label}
                    </Typography>
                    {step.optional && (
                      <span className="text-[11px] text-brown-500">Optional</span>
                    )}
                    {state === "current" ? (
                      <Typography variant="body2" className="text-brown-700">
                        {step.description}
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        className="text-brown-600 opacity-0 transition-opacity duration-150 hover:opacity-100"
                      >
                        {step.description}
                      </Typography>
                    )}
                  </div>
                  {index < steps.length - 1 && (
                    <div className="absolute left-1/2 top-4 hidden h-1 w-full translate-x-1/2 md:block">
                      <div className="h-1 rounded bg-cream-200">
                        <div
                          className={`h-1 rounded ${markerClass}`}
                          style={{ width: state === "complete" ? "100%" : "50%" }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className="md:hidden space-y-2">
          {steps.map((step) => {
            const state = getStepState(step, currentStep);
            if (step.optional && state === "upcoming") {
              const currentStatusStep = getStepForStatus(rawStatus);
              if (currentStatusStep !== step.key) return null;
            }
            return (
              <div key={`${step.key}-mobile`} className="rounded-lg border border-cream-200 bg-cream-50 p-3">
                <div className="flex items-center gap-2">
                  <div className={`h-2 w-2 rounded-full ${renderStatusPill(state)}`} />
                  <Typography variant="subtitle2" className="text-terracotta-800">
                    {step.label}
                  </Typography>
                  {step.optional && (
                    <span className="text-[11px] text-brown-500">Optional</span>
                  )}
                </div>
                {state === "current" && (
                  <Typography variant="body2" className="text-brown-700">
                    {step.description}
                  </Typography>
                )}
              </div>
            );
          })}
        </div>
      </Card.Body>
    </Card>
  );
}

export default MedicalRecordRequestStatusTracker;
